import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotationAPI, projectAPI, categoryAPI, templateAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  Plus,
  Trash,
  Calendar as CalendarIcon,
  MapPin,
  Package,
  CircleNotch,
  PaperPlaneTilt,
  FloppyDisk,
  Upload,
  DownloadSimple,
  FileXls,
  BookmarkSimple,
  BookmarksSimple,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import MatchitoCelebration from '../components/MatchitoCelebration';
import DeliveryMap from '../components/DeliveryMap';
import MaterialImageUpload from '../components/MaterialImageUpload';

const NewProjectPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showMatchito, setShowMatchito] = useState(false);
  const [matchitoMsg, setMatchitoMsg] = useState('');
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    deadline: null,
    delivery_location: {
      latitude: 19.4326,
      longitude: -99.1332,
      radius: 5,
      address: '',
    },
  });
  const [materials, setMaterials] = useState([
    { name: '', category: '', quantity: 1, unit: 'pza', image_url: null }
  ]);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showTemplateName, setShowTemplateName] = useState(false);
  const [templateName, setTemplateName] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchTemplates();
  }, []);

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await quotationAPI.downloadTemplate();
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_materiales.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error al descargar la plantilla');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await projectAPI.uploadExcel(formData);
      const parsed = response.data;
      const newMaterials = parsed.materials.map(m => ({
        name: m.name,
        category: m.category || '',
        quantity: m.quantity || 1,
        unit: m.unit || 'pza',
      }));
      setMaterials(newMaterials);
      toast.success(`${parsed.total_materials} materiales cargados en ${parsed.total_categories} categorías`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el archivo Excel');
    } finally {
      setUploadingExcel(false);
      e.target.value = '';
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await templateAPI.list();
      setTemplates(res.data || []);
    } catch { /* ok */ }
  };

  const handleLoadTemplate = (tmpl) => {
    const mats = tmpl.materials.map(m => ({
      name: m.name,
      category: m.category,
      quantity: m.quantity,
      unit: m.unit,
      image_url: m.image_url || null,
    }));
    setMaterials(mats);
    toast.success(`Plantilla "${tmpl.name}" cargada con ${mats.length} materiales`);
  };

  const handleSaveTemplate = async () => {
    const validMats = materials.filter(m => m.name.trim() && m.category);
    if (validMats.length === 0) {
      toast.error('Agrega al menos un material con nombre y categoría');
      return;
    }
    if (!templateName.trim()) {
      toast.error('Ingresa un nombre para la plantilla');
      return;
    }
    setSavingTemplate(true);
    try {
      await templateAPI.create({
        name: templateName.trim(),
        materials: validMats.map(m => ({
          name: m.name, category: m.category, quantity: m.quantity, unit: m.unit,
          ...(m.image_url && { image_url: m.image_url }),
        })),
      });
      toast.success('Plantilla guardada');
      setShowTemplateName(false);
      setTemplateName('');
      fetchTemplates();
    } catch {
      toast.error('Error al guardar plantilla');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await templateAPI.delete(id);
      toast.success('Plantilla eliminada');
      fetchTemplates();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationFromMap = (loc) => {
    setFormData(prev => ({
      ...prev,
      delivery_location: {
        latitude: loc.latitude,
        longitude: loc.longitude,
        radius: loc.radius,
        address: loc.address,
      },
    }));
  };

  const handleLocationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      delivery_location: { ...prev.delivery_location, [field]: value }
    }));
  };

  const handleMaterialChange = (index, field, value) => {
    setMaterials(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addMaterial = () => {
    setMaterials(prev => [...prev, { name: '', category: '', quantity: 1, unit: 'pza', image_url: null }]);
  };

  const removeMaterial = (index) => {
    if (materials.length === 1) {
      toast.error('Debe haber al menos un material');
      return;
    }
    setMaterials(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('El nombre del proyecto es requerido');
      return false;
    }
    if (!formData.key.trim()) {
      toast.error('La clave del proyecto es requerida');
      return false;
    }
    if (!formData.deadline) {
      toast.error('La fecha límite es requerida');
      return false;
    }
    if (!formData.delivery_location.address.trim()) {
      toast.error('La dirección de entrega es requerida');
      return false;
    }
    
    for (let i = 0; i < materials.length; i++) {
      const m = materials[i];
      if (!m.name.trim()) {
        toast.error(`El nombre del material ${i + 1} es requerido`);
        return false;
      }
      if (!m.category) {
        toast.error(`La categoría del material ${i + 1} es requerida`);
        return false;
      }
      if (m.quantity <= 0) {
        toast.error(`La cantidad del material ${i + 1} debe ser mayor a 0`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (sendImmediately = false) => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        deadline: formData.deadline.toISOString(),
        materials: materials.map(m => ({
          name: m.name,
          category: m.category,
          quantity: parseFloat(m.quantity),
          unit: m.unit,
          ...(m.image_url && { image_url: m.image_url }),
        })),
      };

      const response = await projectAPI.create(payload);
      
      if (sendImmediately) {
        await projectAPI.send(response.data.project_id);
        setMatchitoMsg('¡Proyecto creado y enviado a proveedores!');
        setShowMatchito(true);
        setTimeout(() => navigate(`/projects/${response.data.project_id}`), 4000);
      } else {
        toast.success('Proyecto guardado como borrador');
        navigate(`/projects/${response.data.project_id}`);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear el proyecto';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const commonUnits = ['pza', 'kg', 'm', 'm2', 'm3', 'ml', 'lt', 'ton', 'saco', 'caja', 'rollo', 'tramo', 'cubeta', 'galón'];

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-project-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="rounded-none"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Nuevo Proyecto</h1>
          <p className="text-zinc-600">Crea un proyecto para solicitar cotizaciones</p>
        </div>
      </div>

      {/* Excel Upload Section */}
      <Card className="border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-900/10 rounded-none" data-testid="excel-upload-card">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <FileXls className="w-10 h-10 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">Cargar materiales desde Excel</h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                Descarga la plantilla, llénala con tus materiales y súbela para cargarlos automáticamente.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
                className="rounded-none border-green-300 text-green-700"
                data-testid="download-template-btn"
              >
                {downloadingTemplate ? <CircleNotch className="w-4 h-4 animate-spin" /> : <><DownloadSimple className="w-4 h-4 mr-2" />Plantilla</>}
              </Button>
              <label>
                <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} className="hidden" data-testid="excel-upload-input" />
                <Button
                  variant="outline"
                  className="rounded-none border-orange-300 text-orange-700 cursor-pointer"
                  disabled={uploadingExcel}
                  asChild
                >
                  <span>
                    {uploadingExcel ? <CircleNotch className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" />Subir Excel</>}
                  </span>
                </Button>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project Info */}
      <Card className="border-zinc-200 rounded-none">
        <CardHeader className="border-b border-zinc-200">
          <CardTitle>Información del Proyecto</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Nombre del Proyecto *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Ej: Edificio Reforma 222"
                className="rounded-none mt-1"
                data-testid="project-name"
              />
            </div>
            <div>
              <Label htmlFor="key">Clave del Proyecto *</Label>
              <Input
                id="key"
                value={formData.key}
                onChange={(e) => handleInputChange('key', e.target.value.toUpperCase())}
                placeholder="Ej: REF-222"
                className="rounded-none mt-1 font-mono"
                data-testid="project-key"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Descripción opcional del proyecto..."
              className="rounded-none mt-1"
              rows={3}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Fecha Límite *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full mt-1 justify-start rounded-none font-normal"
                    data-testid="deadline-picker"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {formData.deadline 
                      ? format(formData.deadline, 'PPP', { locale: es })
                      : 'Seleccionar fecha'
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.deadline}
                    onSelect={(date) => handleInputChange('deadline', date)}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <Separator />

            <div>
              <Label htmlFor="address">Dirección de Entrega *</Label>
              <p className="text-xs text-zinc-500 mt-1 mb-2">Haz clic en el mapa o busca una dirección para fijar el punto de entrega</p>
              <DeliveryMap
                latitude={formData.delivery_location.latitude}
                longitude={formData.delivery_location.longitude}
                radius={formData.delivery_location.radius}
                address={formData.delivery_location.address}
                onLocationChange={handleLocationFromMap}
              />
            </div>
        </CardContent>
      </Card>

      {/* Materials */}
      <Card className="border-zinc-200 rounded-none">
        <CardHeader className="border-b border-zinc-200">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Materiales ({materials.length})
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Template selector */}
              {templates.length > 0 && (
                <Select onValueChange={(id) => {
                  const tmpl = templates.find(t => t.template_id === id);
                  if (tmpl) handleLoadTemplate(tmpl);
                }}>
                  <SelectTrigger className="rounded-none w-auto min-w-[180px]" data-testid="template-selector">
                    <BookmarksSimple className="w-4 h-4 mr-1.5 text-orange-500" />
                    <SelectValue placeholder="Usar plantilla..." />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {templates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        <div className="flex items-center justify-between gap-3 w-full">
                          <span>{t.name}</span>
                          <span className="text-xs text-zinc-400">{t.materials_count} mat.</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Save as template */}
              {!showTemplateName ? (
                <Button
                  variant="outline"
                  onClick={() => setShowTemplateName(true)}
                  className="rounded-none text-orange-600 border-orange-200 hover:bg-orange-50"
                  data-testid="save-template-btn"
                >
                  <BookmarkSimple className="w-4 h-4 mr-1.5" />
                  Guardar Plantilla
                </Button>
              ) : (
                <div className="flex items-center gap-1.5" data-testid="template-name-form">
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Nombre de plantilla..."
                    className="rounded-none w-44 h-9 text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveTemplate()}
                    data-testid="template-name-input"
                    autoFocus
                  />
                  <Button size="sm" onClick={handleSaveTemplate} disabled={savingTemplate} className="rounded-none h-9 bg-orange-500 hover:bg-orange-600" data-testid="template-save-confirm">
                    {savingTemplate ? <CircleNotch className="w-4 h-4 animate-spin" /> : 'Guardar'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowTemplateName(false); setTemplateName(''); }} className="rounded-none h-9">
                    &times;
                  </Button>
                </div>
              )}

              <Button 
                variant="outline" 
                onClick={addMaterial}
                className="rounded-none"
                data-testid="add-material-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar Material
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {materials.map((material, index) => (
            <div 
              key={index} 
              className="p-4 border border-zinc-200 space-y-4"
              data-testid={`material-${index}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-zinc-500">Material {index + 1}</span>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => removeMaterial(index)}
                  className="rounded-none text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex gap-4 items-start">
                <MaterialImageUpload
                  imageUrl={material.image_url}
                  onImageChange={(url) => handleMaterialChange(index, 'image_url', url)}
                />
                <div className="flex-1 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Nombre del Material *</Label>
                      <Input
                        value={material.name}
                        onChange={(e) => handleMaterialChange(index, 'name', e.target.value)}
                        placeholder="Ej: Cemento Portland CPC 30R"
                        className="rounded-none mt-1"
                      />
                    </div>
                    <div>
                      <Label>Categoría *</Label>
                      <Select
                        value={material.category}
                        onValueChange={(value) => handleMaterialChange(index, 'category', value)}
                      >
                        <SelectTrigger className="rounded-none mt-1">
                          <SelectValue placeholder="Seleccionar categoría" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px] rounded-none">
                          {categories.map((cat) => (
                            <SelectItem key={cat.category_id} value={cat.name}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Cantidad *</Label>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={material.quantity}
                        onChange={(e) => handleMaterialChange(index, 'quantity', e.target.value)}
                        className="rounded-none mt-1"
                      />
                    </div>
                    <div>
                      <Label>Unidad</Label>
                      <Select
                        value={material.unit}
                        onValueChange={(value) => handleMaterialChange(index, 'unit', value)}
                      >
                        <SelectTrigger className="rounded-none mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          {commonUnits.map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-end">
        <Button 
          variant="outline" 
          onClick={() => handleSubmit(false)}
          disabled={loading}
          className="rounded-none"
          data-testid="save-draft-btn"
        >
          <FloppyDisk className="w-5 h-5 mr-2" />
          Guardar Borrador
        </Button>
        <Button 
          onClick={() => handleSubmit(true)}
          disabled={loading}
          className="btn-primary rounded-none"
          data-testid="send-project-btn"
        >
          {loading ? (
            <CircleNotch className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <PaperPlaneTilt className="w-5 h-5 mr-2" />
              Crear y Enviar a Proveedores
            </>
          )}
        </Button>
      </div>
      <MatchitoCelebration show={showMatchito} message={matchitoMsg} onClose={() => setShowMatchito(false)} />
    </div>
  );
};

export default NewProjectPage;
